import { configureStore, createSlice } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';

import { apiOperation } from './api/common';

import { appsSlice, appsUrl, AppsHolder } from '@/features/apps/appsSlice';
import { ChannelList, ErrorMessage, getChannelsUrl, channelsSlice, getChannelListsUrl,  channelListsSlice, hotelChannelsSlice, hotelChannelListsSlice, ChannelManager, ChannelListManager, ChannelInfo, copyChannelInfo } from '@/features/channels/channelsSlice';
import { loginsSlice, loginUrl, login2faUrl, loginsUrl, LoginDescriptor, LoginInfo, LoginStruct, Login2FARequest, TotpDeviceInfo, LoginPrivileges } from '@/features/logins/loginsSlice';
import { getProjectsByPlatformUrl, getProjectsUrl, limitsUrl, HotelInfo, HotelLimits, HotelInfoPublic, HotelRoom, parseProjectsByPlatformResult, projectsSlice, roomsUrl }  from '@/features/projects/projectsSlice';

import { LanguageManager, LanguagesHolder, languagesUrl, languagesSlice } from '@/features/languages/languagesSlice';
import { MasterInfo, masterUrl, masterSlice, skywayRefreshUrl, SkywayRefreshResult } from '@/features/master/masterSlice';
import { tagsSlice, tagsUrl, TagsHolder } from '@/features/tags/tagsSlice';
import { getProjectGroupsUrl, parseProjectGroups, ProjectGroupRecord, projectGroupsSlice } from '@/features/projectGroups/projectGroupsSlice';
import { AuditHolder, auditSlice, auditUrl } from '@/features/audit/auditSlice';
import { statsInfo, statsSlice } from '@/features/stats/stats';
import { firmwaresSlice, firmwaresUrl, FirmwaresHolder, FirmwareInfo, ProjectFirmwareResponse } from '@/features/firmware/firmwaresSlice';
import { trackFormatsSlice, trackFormatsUrl, TrackFormatsHolder } from '@/features/trackFormats/trackFormatsSlice';
import { ScloneCasting, ScloneEntry, sclonesUrl, scloneDeleteQuery } from '@/features/sclones/sclonesSlice';
import { deletedSlice } from '@/features/deleted/deletedSlice';
import { appUsageSlice } from '@/features/appusage/appUsageSlice';

import { WebSocketService } from '@/app/websocket/WebSocketService';
import { websocketSlice } from '@/features/websocket/websocketSlice';
import type {
  WebSocketConnectionStatus,
  WSServerMessage,
  ChangeEventTarget,
  RoomInfoEventTarget,
  PendingNotification,
  SubscriptionTarget,
} from '@/app/websocket/types';
import {
  HOTEL_SCOPED_TYPES,
  CHANGE_NOTIFICATION_TYPES,
  ROOM_INFO_TYPE,
  WS_SUBSCRIBE_BURST,
  WS_SUBSCRIBE_BURST_GAP_MS,
  isQuietWsError,
} from '@/app/websocket/types';
import {
  SPEED_TEST_NOT_CONNECTED,
  SPEED_TEST_NO_RESULT,
  SPEED_TEST_TIMEOUT_MS,
  extractInfoFields,
  isNewSpeedTestResult,
  readSpeedTestResult,
} from '@/app/websocket/speedTest';

export interface LocalUsersPayload {
  descriptors:				LoginDescriptor[];
  assignable_privileges:	LoginPrivileges[];
}

/*
const loginSlice = createSlice({
  name: 'login',
  initialState: {	login:		-1} as LoginState,
  reducers: {
    assign: (state, action) => {
      //console.log("logicSlice/state=", state, "/action=", action);
      state.login = action.payload.login;
    },
  },
});
*/

export interface CommandInfo {
  command:				string;
  result?:				string;
}

class StoreImpl {
	store:	any;
	
	constructor() {
	  this.store = configureStore({
	    reducer: {
		  // Add your reducers here
		  logins:				loginsSlice.reducer,
	      channels:				channelsSlice.reducer,
	      hotelChannels:		hotelChannelsSlice.reducer,
	      channelLists:			channelListsSlice.reducer,
	      hotelChannelLists:	hotelChannelListsSlice.reducer,
	      projects:				projectsSlice.reducer,
	      apps:					appsSlice.reducer,
	      languages:			languagesSlice.reducer,
	      master:				masterSlice.reducer,
	      tags:					tagsSlice.reducer,
	      projectGroups:		projectGroupsSlice.reducer,
	      audit:				auditSlice.reducer,
	      stats:				statsSlice.reducer,
	      firmwares:			firmwaresSlice.reducer,
	      trackFormats:		trackFormatsSlice.reducer,
	      websocket:			websocketSlice.reducer,
	      deleted:				deletedSlice.reducer,
	      appusage:				appUsageSlice.reducer,
		},
	  });
	  
	  this.refresh();
	}
	
	async refresh() {
	  const login = await this.getLogin();
	  this.getProjects();
	  if (login && (login.role === 'admin' || login.privileges?.includes('ViewChannels'))) {
	    this.getChannels();
	  }
	  if (login && (login.role === 'admin' || login.privileges?.includes('ViewLanguages'))) {
	    this.getLanguages();
	  }
	  this.getLogins();

	  // Connect WebSocket ONLY after login is confirmed
	  if (login && !this.ws) {
	    setTimeout(() => this.connectWebSocket(), 500);
	  }
	}
	
	dispatchStatus(status: number) {
      const payload = loginsSlice.actions.setStatus(status);
      
      this.store.dispatch(payload);
	}
	
	dispatchError(err: string) {
      const payload = loginsSlice.actions.setError(err);
      
      this.store.dispatch(payload);
	}
	
	dispatchSuccess(succ: string) {
      const payload = loginsSlice.actions.setSuccess(succ);
      
      this.store.dispatch(payload);
	}
	
	async getLogins() {
      const data = await apiOperation("GET", loginsUrl, null);
	  
      if (	!data ||
          	!data.result
         )
        return;
  
      this.dispatchLogins(data.result);
    }
    
	dispatchLogins(logins: LoginStruct) {
      const payload = loginsSlice.actions.setLogins(logins);
      
      this.store.dispatch(payload);
	}
	
	async getLogin(): Promise<LoginDescriptor | null> {
      const data = await apiOperation("GET", loginUrl, null);

      if (	!data ||
          	!data.result
         )
        return null;

      this.captureSupports2FA(data.result);
      this.dispatchLogin(data.result);
      return data.result;
    }

	private captureSupports2FA(result: any) {
	  if (result && typeof result.supports_2fa === "boolean") {
	    this.store.dispatch(loginsSlice.actions.setSupports2FA(result.supports_2fa));
	  }
	}
    
	dispatchLogin(login: LoginDescriptor) {
      const payload = loginsSlice.actions.setLogin(login);

      this.store.dispatch(payload);
	}

	async performLogin(user: string, pass: string, rememberMe?: boolean): Promise<string> {
	  const body: LoginInfo = {
	    username:				user,
	    password:				pass,
	    remember_me:			!!rememberMe,
	    supports_2fa:			true,
	  };

      const data = await apiOperation("POST", "/login.json", body);

	  if (!data.result)
	    return "failure";

	  this.captureSupports2FA(data.result);

	  // GitHub-style 2FA: if the server says we need a second step, stash
	  // the ticket in Redux and let the Login page render a code prompt.
	  // No cookies are set on this response — we are NOT logged in yet.
	  if (data.result.requires_2fa) {
	    this.store.dispatch(loginsSlice.actions.set2FAChallenge(data.result));
	    this.dispatchStatus(0);
	    return "requires_2fa";
	  }

	  this.refresh();
	  this.connectWebSocket();
	  return "success";
	}

	// performLogin2FA submits the 6-digit TOTP (or an xxxx-xxxx recovery
	// code) together with the ticket from step 1. On success the server
	// sets the auth cookies and we proceed with the normal post-login
	// refresh. Returns "no_ticket" if called without a stashed ticket,
	// "failure" if the server rejected the code, "success" on a clean
	// login.
	async performLogin2FA(code: string, method: string = "totp"): Promise<string> {
	  const state = this.store.getState();
	  const ticket: string = state.logins.ticket;
	  if (!ticket) {
	    return "no_ticket";
	  }

	  const body: Login2FARequest = { ticket, code, method };
	  const data = await apiOperation("POST", login2faUrl, body);
	  // No console.log — the ticket above IS a credential and we don't
	  // want to ever emit it to the browser console.

	  // Lockout path: server returns 401 with { locked_until: RFC3339 }
	  // after 5 failed attempts. apiOperation doesn't read the 401 body,
	  // so we parse it here and stash the timestamp so the Login screen
	  // can disable the input and show a countdown.
	  if (!data.result && data.response && data.response.status === 401) {
	    try {
	      const lockBody = await data.response.json();
	      if (lockBody && typeof lockBody.locked_until === "string" && lockBody.locked_until) {
	        this.store.dispatch(loginsSlice.actions.setLockout(lockBody.locked_until));
	        return "locked";
	      }
	    } catch (_e) {
	      // Body wasn't JSON or already consumed — fall through to generic failure.
	    }
	  }

	  if (!data.result) {
	    return "failure";
	  }

	  // Surface recovery-codes-remaining if the server returned it
	  // (recovery code path) so the UI can warn the user to regenerate
	  // when low.
	  if (typeof data.result.recovery_codes_remaining === "number") {
	    this.store.dispatch(
	      loginsSlice.actions.setRecoveryCodesRemaining(data.result.recovery_codes_remaining)
	    );
	  }

	  // Order matters: refresh the cached descriptor BEFORE clearing the
	  // 2FA challenge state. Otherwise App.tsx briefly sees status=1,
	  // requires2FA=false, login.totp_enrolled=undefined (stale) and
	  // routes to EnrollTwoFA for a render frame.
	  await this.refresh();
	  this.connectWebSocket();
	  this.store.dispatch(loginsSlice.actions.clear2FAChallenge());
	  return "success";
	}


	async sendLogin2FAEmailCode(): Promise<string> {
	  const state = this.store.getState();
	  const ticket: string = state.logins.ticket;
	  if (!ticket) {
	    return "no_ticket";
	  }

	  const data = await apiOperation("POST", login2faUrl + "?action=send_email_code", { ticket });
	  if (data.result && data.result.sent) {
	    return "ok";
	  }
	  if (data.response && data.response.status === 429) {
	    return "locked";
	  }
	  return "failure";
	}

	async setPreferred2FAMethod(method: "email" | "totp"): Promise<boolean> {
	  const data = await apiOperation("POST", "/2fa.json?action=set_method", { method });
	  if (!data.result) {
	    return false;
	  }
	  await this.refreshLoginDescriptor();
	  return true;
	}


	cancel2FAChallenge() {
	  this.store.dispatch(loginsSlice.actions.clear2FAChallenge());
	}

	clearLockout() {
	  this.store.dispatch(loginsSlice.actions.clearLockout());
	}

	async startEnrollment(): Promise<{
	  setup_token: string;
	  otpauth_uri: string;
	  qr_png_base64: string;
	  manual_entry_key: string;
	  expires_at: string;
	} | null> {
	  const data = await apiOperation("POST", "/2fa.json?action=start", {});

	  if (!data.result) {
	    if (data.response && data.response.status === 401) {
	      this.dispatchError("Could not start two-factor setup — your session may have expired. Please sign in again.");
	    }
	    return null;
	  }
	  return data.result;
	}

	// verifyEnrollment finalises 2FA setup by submitting the code that
	// the user reads from their authenticator app. Returns:
	//   - device_id, device_name   (always)
	//   - recovery_codes           (only when this was the user's first device)
	//
	// We deliberately do NOT call getLogin() here. Refreshing the cached
	// descriptor would cause App.tsx to re-route immediately on
	// first-device enrollment, unmounting the recovery-codes view before
	// the user can read them. The descriptor refreshes via
	// refreshLoginDescriptor() after the user dismisses the codes.
	async verifyEnrollment(setupToken: string, code: string, deviceName?: string): Promise<{
	  device_id: string;
	  device_name: string;
	  recovery_codes?: string[];
	} | null> {
	  const data = await apiOperation("POST", "/2fa.json?action=verify", {
	    setup_token:	setupToken,
	    code:			code,
	    device_name:	deviceName || "",
	  });
	  // Response body contains recovery_codes (single-use secrets) on
	  // the first-device path. Skip the console.log.
	  if (!data.result || !data.result.device_id) return null;
	  return data.result;
	}

	// refreshLoginDescriptor re-fetches GET /login.json and updates the
	// Redux cache. Used by the enrollment flow AFTER the user has
	// dismissed the recovery-codes screen so totp_enrolled becomes true
	// in the cache without unmounting the codes view prematurely.
	async refreshLoginDescriptor(): Promise<void> {
	  await this.getLogin();
	}

	// listDevices returns the user's enrolled authenticator devices (no
	// secrets). Used by the 2FA settings page to render the device list.
	async listDevices(): Promise<TotpDeviceInfo[]> {
	  const data = await apiOperation("GET", "/2fa.json?action=devices", null);
	  if (!data.result || !Array.isArray(data.result.devices)) return [];
	  return data.result.devices;
	}

	// removeDevice deletes one authenticator. Requires a current code
	// from a DIFFERENT enrolled device (server enforces). Returns true
	// on success.
	async removeDevice(deviceID: string, code: string): Promise<boolean> {
	  const data = await apiOperation("POST", "/2fa.json?action=remove", {
	    device_id:	deviceID,
	    code:		code,
	  });
	  return !!(data.result && data.result.removed);
	}

	// regenerateRecoveryCodes invalidates the previous 10 recovery codes
	// and issues a fresh set. The server requires a CURRENT TOTP code
	// (recovery codes cannot regenerate themselves — they'd be a privilege
	// escalation path). Returns the new codes array on success, null on
	// any failure (wrong code, lockout, server error). The caller must
	// surface the codes via a "save once" modal — they are not retrievable
	// later.
	async regenerateRecoveryCodes(code: string): Promise<string[] | null> {
	  const data = await apiOperation("POST", "/2fa.json?action=regenerate", { code });
	  if (!data.result || !Array.isArray(data.result.recovery_codes)) return null;
	  // Reset the "low recovery codes" warning — the user now has 10 fresh ones.
	  this.store.dispatch(loginsSlice.actions.setRecoveryCodesRemaining(data.result.recovery_codes.length));
	  return data.result.recovery_codes as string[];
	}

	// adminSetTotpDisabled is the admin-only flip for the TotpDisabled
	// override flag. Calling with disabled=true exempts the target user
	// from 2FA entirely (no enrollment screen, no login prompt) AND
	// wipes any existing TOTP record. Calling with disabled=false
	// re-enables: TotpDisabled is cleared and the stored record is wiped
	// — so the user is forced through fresh enrollment (new QR, new
	// recovery codes) on their next login.
	async adminSetTotpDisabled(username: string, disabled: boolean, projectId?: string): Promise<boolean> {
	  const path = disabled ? "/2fa/admin/disable.json" : "/2fa/admin/enable.json";
	  const body: { username: string; project_id?: string } = { username };
	  if (projectId) body.project_id = projectId;
	  const data = await apiOperation("POST", path, body);
	  if (!data.result) return false;
	  // Refresh the relevant list so the cached LoginDescriptor reflects
	  // the new flags. For global users, refresh /logins.json; for local
	  // users, the parent LocalUsers component re-fetches via its own
	  // refresh path so we just return success here.
	  if (!projectId) {
	    await this.getLogins();
	  }
	  return true;
	}



	async getLocalUsers(projectId: string): Promise<LocalUsersPayload> {
	  const url = `/local_users.json?project_id=${encodeURIComponent(projectId)}`;
	  const data = await apiOperation("GET", url, null);
	  if (!data.result) return { descriptors: [], assignable_privileges: [] };

	  return {
	    descriptors: Array.isArray(data.result.descriptors)
	      ? data.result.descriptors as LoginDescriptor[]
	      : [],
	    assignable_privileges: Array.isArray(data.result.assignable_privileges)
	      ? data.result.assignable_privileges as LoginPrivileges[]
	      : [],
	  };
	}

	async setLocalUser(
	  projectId: string,
	  user: LoginDescriptor,
	  isNew: boolean,
	  privileges?: string[],
	): Promise<boolean> {
	  const action = isNew ? "create" : "update";
	  const url = `/local_users.json?project_id=${encodeURIComponent(projectId)}&action=${action}`;
	  const body: {
	    username:		string;
	    email?:			string;
	    password?:		string;
	    privileges?:	string[];
	  } = isNew
	    ? { username: user.username, password: user.password, email: user.email }
	    : { username: user.username, email: user.email };

	  if (privileges !== undefined) body.privileges = privileges;

	  const data = await apiOperation("POST", url, body);
	  if (!data.result) return false;
	  return !!(data.result.errors && data.result.errors.length && data.result.errors[0].code == 0);
	}

	async resetLocalUserPassword(projectId: string, username: string, newPassword: string): Promise<boolean> {
	  const url = `/local_users.json?project_id=${encodeURIComponent(projectId)}&action=reset_password`;
	  const data = await apiOperation("POST", url, { username, new_password: newPassword });
	  if (!data.result) return false;
	  return !!(data.result.errors && data.result.errors.length && data.result.errors[0].code == 0);
	}

	async deleteLocalUser(projectId: string, username: string): Promise<boolean> {
	  const url = `/local_users.json?project_id=${encodeURIComponent(projectId)}&action=delete`;
	  const data = await apiOperation("POST", url, { username });
	  if (!data.result) return false;
	  return !!(data.result.errors && data.result.errors.length && data.result.errors[0].code == 0);
	}

	// ===== Clone settings (sClone) — global catalog =====
	// Encrypted Samsung Tizen clone bundles keyed by model + firmware version
	// (no project scope), managed via /sclones.json. Mirrors the local-users
	// helpers: list returns the array, mutations return their success flag. The
	// catalog is small and refetched after each change, so it lives in the
	// page's local state rather than Redux.

	async getSclones(): Promise<ScloneEntry[]> {
	  const data = await apiOperation("GET", sclonesUrl, null);
	  // GET returns a BARE array, not the { errors: [...] } envelope. On
	  // 401/403/500 apiOperation already fired the toast / bumped status and
	  // returns result=null — treat that as an empty catalog.
	  if (!data.result || !Array.isArray(data.result)) return [];
	  return data.result as ScloneEntry[];
	}

	async uploadSclone(
	  zip: File,
	  casting: ScloneCasting | null,
	  onProgress?: (loaded: number, total: number) => void,
	): Promise<{ ok: boolean; error?: string; entry?: ScloneEntry }> {
	  // Use XMLHttpRequest, not fetch: fetch can't report UPLOAD progress, and
	  // the page shows a live progress ring. Also bypasses apiOperation (which
	  // JSON-stringifies its payload) so we can send multipart and still read
	  // the structured envelope back on a 4xx. Send the `file` part plus the
	  // `casting` the clone was exported with (0 OFF / 1 ON) — the backend
	  // reads model/version from ProductCloneInfo.txt inside the zip. casting
	  // is null only for an older backend that has no casting (it would ignore
	  // it anyway); without it a newer server stores a bundle for BOTH
	  // settings. Never set Content-Type by hand; XHR adds the multipart boundary.
	  const fd = new FormData();
	  fd.append("file", zip);
	  if (casting !== null) fd.append("casting", String(casting));

	  return new Promise((resolve) => {
	    const xhr = new XMLHttpRequest();
	    xhr.open("POST", sclonesUrl, true);
	    xhr.withCredentials = true;

	    if (onProgress && xhr.upload) {
	      xhr.upload.onprogress = (evt: ProgressEvent) => {
	        if (evt.lengthComputable) onProgress(evt.loaded, evt.total);
	      };
	    }

	    xhr.onload = () => {
	      // Expired session → 401 plain-text. Kick to login, matching apiOperation.
	      if (xhr.status === 401) {
	        this.dispatchStatus(0);
	        resolve({ ok: false, error: "Authentication required" });
	        return;
	      }

	      // Try the JSON envelope first. The plain-text gates (403 missing
	      // privilege, middleware's 413 request-too-large, 405) aren't JSON, so
	      // JSON.parse throws — fall back to the raw text for those.
	      let body: any = null;
	      try { body = JSON.parse(xhr.responseText); } catch (_e) { /* not JSON */ }

	      const err = body && Array.isArray(body.errors) && body.errors.length ? body.errors[0] : null;
	      if (err) {
	        if (err.code === 0) {
	          this.dispatchSuccess(err.error);
	          resolve({ ok: true, entry: err.result as ScloneEntry });
	          return;
	        }
	        this.dispatchError(err.code + ': ' + err.error);
	        resolve({ ok: false, error: err.error });
	        return;
	      }

	      // No envelope → a plain-text gate. Surface the text body.
	      const msg = (xhr.responseText || xhr.statusText || `Request failed (${xhr.status})`).trim();
	      this.dispatchError(msg);
	      resolve({ ok: false, error: msg });
	    };

	    xhr.onerror = () => {
	      const msg = "Network error";
	      this.dispatchError(msg);
	      resolve({ ok: false, error: msg });
	    };

	    xhr.send(fd);
	  });
	}

	async deleteSclone(model: string, version: string, casting: ScloneCasting | null): Promise<boolean> {
	  // casting null = the "Both" bundle, addressed by leaving casting out.
	  const url = sclonesUrl + scloneDeleteQuery(model, version, casting);
	  const data = await apiOperation("DELETE", url, null);
	  // Idempotent server-side: deleting a missing bundle still returns code 0.
	  if (!data.result) return false;
	  return !!(data.result.errors && data.result.errors.length && data.result.errors[0].code == 0);
	}

	async setLogin(login: LoginDescriptor): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", loginsUrl, login);
	  
	  console.log("StoreImpl/setLogin/data=", data);
	  if (!data.result) {
	    return null;
	  }
	  
	  const mess = data.result;
	  
	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getLogins();
	  
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	// changeOwnPassword lets the currently-logged-in user change their own
	// password. Picks the right URL based on whether they're a global or
	// local user (local_project_id on their LoginDescriptor). On success
	// the server keeps the caller's session_token alive but revokes all
	// the user's OTHER sessions — so the SPA stays signed in but other
	// tabs/devices need to log in again.
	//
	// Returns the parsed error envelope so callers can branch on code
	// (4=wrong current, 5=complexity fail, 6=same as current, etc.).
	async changeOwnPassword(currentPassword: string, newPassword: string): Promise<{
	  ok: boolean;
	  code: number;
	  message: string;
	}> {
	  const state = this.store.getState();
	  const login = state.logins.login;
	  const url = login && login.local_project_id
	    ? `/local_users.json?project_id=${encodeURIComponent(login.local_project_id)}&action=change_password`
	    : `/logins.json?action=change_password`;
	  // Bypass apiOperation here. On a 400 response apiOperation reads the
	  // body to fire the global error toast and then returns result=null —
	  // we'd lose the structured {code, error} envelope we need to map to
	  // inline form errors (e.g. code 4 = wrong current password).
	  try {
	    const response = await fetch(url, {
	      method:		"POST",
	      headers:	{ "Content-Type": "application/json" },
	      body:		JSON.stringify({
	        current_password:	currentPassword,
	        new_password:		newPassword,
	      }),
	    });
	    let body: any = null;
	    try { body = await response.json(); } catch (_e) { /* not JSON */ }
	    const err = body && Array.isArray(body.errors) && body.errors.length > 0 ? body.errors[0] : null;
	    if (err) {
	      return { ok: err.code === 0, code: err.code, message: err.error };
	    }
	    if (!response.ok) {
	      return { ok: false, code: -1, message: `Server responded ${response.status}` };
	    }
	    return { ok: false, code: -1, message: "Unexpected response from server" };
	  } catch (e) {
	    return { ok: false, code: -1, message: "Network error" };
	  }
	}

	// refreshSession is the "Stay logged in" action — any authenticated HTTP
	// call resets the server's LastSeen timer and clears its "warning sent"
	// flag (spec §3). We use the cheapest endpoint, GET /login.json.
	// Returns true if the session is still alive, false if the backend has
	// already evicted us (401) so the caller can fall through to logout.
	async refreshSession(): Promise<boolean> {
	  try {
	    const response = await fetch('/login.json', { credentials: 'include' });
	    if (response.ok) {
	      this.store.dispatch(websocketSlice.actions.clearSessionExpiring());
	      return true;
	    }
	    if (response.status === 401) return false;
	    return false;
	  } catch (_e) {
	    return false;
	  }
	}

	clearSessionExpiring() {
	  this.store.dispatch(websocketSlice.actions.clearSessionExpiring());
	}

	async performLogout() {
	  this.disconnectWebSocket();
	  // Drop any in-flight enrollment session so the next login starts
	  // with a fresh QR — otherwise stale sessionStorage from a previous
	  // user/session can short-circuit EnrollTwoFA's first-mount logic.
	  try { sessionStorage.removeItem('enrollTwoFA_session'); } catch (_e) { /* ignore */ }
	  const data = await apiOperation("GET", "/login.json?logout=yes", null);
	  console.log("StoreImpl/performLogout/GET/data=", data);

	  this.refresh();
	}
	
	async getChannels(hotel_id?: string, global?: boolean) {
      const url =	getChannelsUrl(hotel_id, global);
  
      const data = await apiOperation("GET", url, null);
	  
      if (	!data ||
          	!data.result
         )
        return;
  
      this.dispatchChannels(data.result, hotel_id);
    }
    
    // Same request as getChannels(), but the payload is returned instead of
    // dispatched. Callers that need the hotel channels merged with the global
    // ones (global=1) must not push that mix into the hotelChannels slice: pages
    // like Channels render the slice verbatim as the project's own channels.
    async fetchChannels(hotel_id?: string, global?: boolean): Promise<ChannelManager | null> {
      const url =	getChannelsUrl(hotel_id, global);
  
      const data = await apiOperation("GET", url, null);
	  
      if (	!data ||
          	!data.result
         )
        return null;
  
      return data.result as ChannelManager;
    }
    
    dispatchChannels(channels: ChannelManager, hotel_id?: string) {
      const payload =	hotel_id &&
      					hotel_id != ""
      					?	hotelChannelsSlice.actions.assign(channels)
      					:	channelsSlice.actions.assign(channels);
      
      this.store.dispatch(payload);
	}
	
	async setChannel(channel: ChannelInfo, hotel_id?: string): Promise<ErrorMessage | null> {
	  const url =	getChannelsUrl(hotel_id);
	  
	  const data = await apiOperation("POST", url, channel);
	  
	  console.log("StoreImpl/setChannel/data=", data);
	  if (!data.result) {
	    return null; //await data.response.text())
	  }
	  
	  const mess = data.result;
	  
	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getChannels(hotel_id);
	  
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	async getChannelLists(hotel_id?: string, global?: boolean) {
      const url =	getChannelListsUrl(hotel_id, global);
  
      const data = await apiOperation("GET", url, null);
	  
      if (	!data ||
          	!data.result
         )
        return;
  
      this.dispatchChannelLists(data.result, hotel_id);
    }
    
    dispatchChannelLists(lists: ChannelListManager, hotel_id?: string) {
      const payload =	hotel_id &&
      					hotel_id != ""
      					?	hotelChannelListsSlice.actions.assign(lists)
      					:	channelListsSlice.actions.assign(lists);
      
      this.store.dispatch(payload);
	}
	
	async setChannelList(list: ChannelList, hotel_id?: string): Promise<ErrorMessage | null> {
	  const url =	getChannelListsUrl(hotel_id);

	  const data = await apiOperation("POST", url, list);

	  console.log("StoreImpl/setChannelList/data=", data);
	  if (!data.result) {
	    return {
	      errors: [{
	        code: data.response.status,
	        error: data.response.statusText || 'Request failed',
	      }]
	    };
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getChannelLists(hotel_id);

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	async verifyAndSaveChannelList(list: ChannelList, hotel_id?: string, ignoreUnavailable?: boolean): Promise<ErrorMessage | null> {
	  let url = getChannelListsUrl(hotel_id);
	  const verifyParam = ignoreUnavailable ? 'verify=ignore' : 'verify=on';
	  url += (url.includes('?') ? '&' : '?') + verifyParam;

	  const data = await apiOperation("POST", url, list);

	  console.log("StoreImpl/verifyAndSaveChannelList/data=", data);
	  if (!data.result) {
	    return {
	      errors: [{
	        code: data.response.status,
	        error: data.response.statusText || 'Request failed',
	      }]
	    };
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getChannelLists(hotel_id);

	return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	async getProjects(hotel_id?: string) {
      if (hotel_id === null) {
        this.dispatchProjectActive(null);
        return;
      }
      
      const url = getProjectsUrl(hotel_id);
  
      const data = await apiOperation("GET", url, null);
	  
      if (!data.result)
        return;
  
      if (hotel_id) {
        this.dispatchProjectActive(data.result);
      } else {
        this.dispatchProjects(data.result);
      }
    }
    
    dispatchProjects(projects: HotelInfo[]) {
      const payload =	projectsSlice.actions.setProjects(projects);
      
      this.store.dispatch(payload);
	}
	
    dispatchProjectActive(active: HotelInfoPublic | null) {
      const payload =	projectsSlice.actions.setActive(active);
      
      this.store.dispatch(payload);
	}
	
	async getLanguages() {
      const data = await apiOperation("GET", languagesUrl, null);
	  
      if (!data.result)
        return;
  
      this.dispatchLanguages(data.result);
    }
    
    dispatchLanguages(languages: LanguageManager) {
      const payload =	languagesSlice.actions.assign(languages);
      this.store.dispatch(payload);
	}

	async setLanguages(languages: LanguagesHolder): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", languagesUrl, languages);

	  console.log("StoreImpl/setLanguages/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getLanguages();

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	async cloneProject(source_id: string, name: string): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", "/clone.json", {
	    type:	"Hotel",
	    id:		source_id,
	    name:	name,
	  });

	  console.log("StoreImpl/cloneProject/result=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    setTimeout(() => this.getProjects(), 500);

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	async setProject(project: HotelInfo): Promise<ErrorMessage | null> {
	  const url =	getProjectsUrl();
	  
	  const data = await apiOperation("POST", url, project);
	  
	  console.log("StoreImpl/setProject/result=", data);
	  if (!data.result) {
	    return null; //(await data.response.text());
	  }
	  
	  const mess = data.result;
	  
	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 ) {
	    setTimeout(() => this.getProjects(), 500);
	    // Task-80 §4.1 — a move changes the groups' member lists and sums.
	    if (project.group !== undefined)
	      setTimeout(() => this.refreshProjectGroups(), 500);
	  } else
	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].error === "Unknown project group"
  		 )
	    // §4.3 — the group vanished meanwhile: refresh the dropdown's options.
	    this.getProjectGroups();
	  
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	// ---- Project groups (Task-80) ---------------------------------------------

	async getProjectGroups() {
	  const data = await apiOperation("GET", getProjectGroupsUrl(), null);

	  if (	!data ||
	      	!data.result
	     )
	    return;

	  this.store.dispatch(projectGroupsSlice.actions.setGroups(parseProjectGroups(data.result)));
	}

	// The drill-down's list: /hotels.json?group=. Lands in projectGroups, never
	// in projects.projects — that one stays the flat estate (header tiles,
	// the project page's lookup by id). null leaves the drill-down.
	async getProjectGroupMembers(group_id: string | null) {
	  if (!group_id) {
	    this.store.dispatch(projectGroupsSlice.actions.setActive({ active: null, members: null }));
	    return;
	  }

	  const data = await apiOperation("GET", getProjectsUrl(undefined, false, group_id), null);

	  if (	!data ||
	      	!data.result
	     )
	    return;

	  // Ignore a late answer for a group the user has already left.
	  const active = this.store.getState().projectGroups.active;
	  if (	active !== null &&
	      	active !== group_id
	     )
	    return;

	  this.store.dispatch(projectGroupsSlice.actions.setActive({
	    active:		group_id,
	    members:	parseProjectsByPlatformResult(data.result),
	  }));
	}

	enterProjectGroup(group_id: string | null) {
	  this.store.dispatch(projectGroupsSlice.actions.setActive({ active: group_id, members: null }));
	  if (group_id)
	    this.getProjectGroupMembers(group_id);
	}

	refreshProjectGroups() {
	  this.getProjectGroups();
	  const active = this.store.getState().projectGroups.active;
	  if (active)
	    this.getProjectGroupMembers(active);
	}

	async setProjectGroup(record: ProjectGroupRecord): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", getProjectGroupsUrl(), record);

	  console.log("StoreImpl/setProjectGroup/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 ) {
	    this.refreshProjectGroups();
	    // A disband ungroups the members, so the flat list changes too.
	    this.getProjects();
	  }

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	async setLimits(limits: HotelLimits): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", limitsUrl, limits);
	  
	  console.log("StoreImpl/setLimit/result=", data);
	  if (!data.result) {
	    return null; //(await data.response.text());
	  }
	  
	  const mess = data.result;
	  
	  //if (checkNoError(mess))
	  //this.getProjects(limits.id);
	  
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	async getRooms(hotelId: string) {
	  const url = `${roomsUrl}?id=${encodeURIComponent(hotelId)}`;
	  const data = await apiOperation("GET", url, null);

	  console.log("StoreImpl/getRooms/hotelId=" + hotelId + "/data=", data);
	  if (!data.result)
	    return;

	  // data.result is the RoomManager (has id, rooms[]) — merge into active project
	  const current = this.store.getState().projects.active;
	  if (current) {
	    this.dispatchProjectActive({ ...current, rooms: data.result });
	  }
	}

	async getRoom(hotelId: string, roomNo: string) {
	  const url = `${roomsUrl}?id=${encodeURIComponent(hotelId)}&room_no=${encodeURIComponent(roomNo)}`;
	  const data = await apiOperation("GET", url, null);

	  console.log("StoreImpl/getRoom/hotelId=" + hotelId + "/roomNo=" + roomNo + "/data=", data);
	  if (!data.result)
	    return;

	  // data.result is a single HotelRoom — merge into existing rooms array
	  const current = this.store.getState().projects.active;
	  if (current && current.rooms) {
	    const updatedRoom = data.result as HotelRoom;
	    const existingRooms = current.rooms.rooms || [];
	    const newRooms = existingRooms.map((r: HotelRoom) =>
	      r.room_no === updatedRoom.room_no ? updatedRoom : r
	    );
	    // If room not found in existing list, add it
	    if (!existingRooms.some((r: HotelRoom) => r.room_no === updatedRoom.room_no)) {
	      newRooms.push(updatedRoom);
	    }
	    this.dispatchProjectActive({
	      ...current,
	      rooms: { ...current.rooms, rooms: newRooms },
	    });
	  }
	}

	async setRoom(room: HotelRoom): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", roomsUrl, room);
	  
	  console.log("StoreImpl/setRoom/result=", data);
	  if (!data.result) {
	    return null; //(await data.response.text());
	  }
	  
	  const mess = data.result;
	  
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	async getApps() {
      const data = await apiOperation("GET", appsUrl, null);
	  
      if (	!data ||
          	!data.result
         )
        return;
  
      this.dispatchApps(data.result);
    }
    
    dispatchApps(apps: AppsHolder) {
      const payload =	appsSlice.actions.assign(apps);
      
      this.store.dispatch(payload);
	}
	
	async setApps(apps: AppsHolder): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", appsUrl, apps);
	  
	  console.log("StoreImpl/setApps/data=", data);
	  if (!data.result) {
	    return null;
	  }
	  
	  const mess = data.result;
	  
	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getApps();
	  
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	
	async getMaster() {
      const data = await apiOperation("GET", masterUrl, null);
	  
      if (	!data ||
          	!data.result
         )
        return;
  
      this.dispatchMaster(data.result);
    }
    
    dispatchMaster(master: MasterInfo) {
      const payload =	masterSlice.actions.assign(master);
      
      this.store.dispatch(payload);
	}
	
	async setMaster(master: Partial<MasterInfo>): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", masterUrl, master);

	  if (!data.result) {
	    return null;
	  }
	  
	  const mess = data.result;
	  
	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getMaster();
	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	async getTags() {
      const data = await apiOperation("GET", tagsUrl, null);

      if (	!data ||
          	!data.result
         )
        return;

      this.dispatchTags(data.result);
    }

    dispatchTags(tags: TagsHolder) {
      const payload =	tagsSlice.actions.assign(tags);

      this.store.dispatch(payload);
	}

	async setTags(tags: TagsHolder): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", tagsUrl, tags);

	  console.log("StoreImpl/setTags/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 ) {
	    this.dispatchTags(tags);
	    this.getTags();
	  }

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}
	async getProjectList(tags: TagsHolder): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", tagsUrl, tags);

	  console.log("StoreImpl/setTags/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getTags();

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	async getChannelListByProject(epg_id: string): Promise<HotelInfo[] | null> {
	  const url = `/channel_lists.json?epg_id=${encodeURIComponent(epg_id)}`;
	  const data = await apiOperation("GET", url, null);

	  console.log("StoreImpl/getProjectsByChannelList/epg_id=" + epg_id + "/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const projects = data.result.projects || [];
	  this.dispatchChannelListProjects(projects);
	  return projects;
	}

	async getProjectByEpgID(epg_id:string): Promise<HotelInfo[] | null>{
		const url=`/channels.json?epg_id=${encodeURIComponent(epg_id)}`;
		const data=await apiOperation("GET",url,null);
		console.log("StoreImpl/getProjectByEpgID/epg_id=" + epg_id + "/data=", data);
		if (!data.result) {
			return null;
		}

		const projects = data.result.projects || [];
		this.dispatchChannelListProjects(projects);
		return projects;
	}

	async getProjectsByPlatform(platform: string): Promise<HotelInfo[] | null> {
	  const url = getProjectsByPlatformUrl(platform);
	  if (!url) {
	    // An empty platform returns EVERY project server-side, not an empty
	    // list — never send one. Treat it as an empty result locally.
	    this.dispatchChannelListProjects([]);
	    return [];
	  }

	  const data = await apiOperation("GET", url, null);

	  console.log("StoreImpl/getProjectsByPlatform/platform=" + platform + "/data=", data);
	  if (!data.result)
	    return null;

	  // Dispatch into channelListProjects, never setProjects — setProjects
	  // recomputes the global platform aggregation and would rewrite the
	  // statistics table with the filtered subset.
	  const projects = parseProjectsByPlatformResult(data.result);
	  this.dispatchChannelListProjects(projects);
	  return projects;
	}

	async getStatsData():Promise<statsInfo[] | null>{
		const url=`/stats.json`
		const data=await apiOperation("GET",url,null);
		if(!data.result){
			return null
		}
		const channel=data.result.channels || [];
		this.dispatchStatsInfo(channel)
		return channel;
	}

	dispatchStatsInfo(channels: statsInfo[]) {
		const payload = statsSlice.actions.setStats(channels);
		this.store.dispatch(payload);
	}

	async getChannelListByRoom(hotel_id: string, epg_id: string): Promise<HotelRoom[] | null> {
	  const url = `/channel_lists.json?hotel_id=${encodeURIComponent(hotel_id)}&epg_id=${encodeURIComponent(epg_id)}`;
	  const data = await apiOperation("GET", url, null);

	  console.log("StoreImpl/getChannelListByRoom/hotel_id=" + hotel_id + "/epg_id=" + epg_id + "/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const rooms = data.result.rooms || [];
	  this.dispatchChannelListRooms(rooms);
	  return rooms;
	}

	dispatchChannelListRooms(rooms: HotelRoom[]) {
	  const payload = projectsSlice.actions.setChannelListRooms(rooms);
	  this.store.dispatch(payload);
	}

	clearChannelListRooms() {
	  const payload = projectsSlice.actions.setChannelListRooms(null);
	  this.store.dispatch(payload);
	}

	dispatchChannelListProjects(projects: HotelInfo[]) {
	  const payload = projectsSlice.actions.setChannelListProjects(projects);
	  this.store.dispatch(payload);
	}

	clearChannelListProjects() {
	  const payload = projectsSlice.actions.setChannelListProjects(null);
	  this.store.dispatch(payload);
	}
	async getFirmwares() {
      console.log("store.getFirmwares called - URL:", firmwaresUrl);
      const data = await apiOperation("GET", firmwaresUrl, null);
      console.log("store.getFirmwares response:", data);

      if (	!data ||
          	!data.result
         )
        return;

      this.dispatchFirmwares(data.result);
    }

    dispatchFirmwares(firmwares: FirmwaresHolder) {
      const payload =	firmwaresSlice.actions.assign(firmwares);

      this.store.dispatch(payload);
	}

	async setFirmware(firmware: FirmwareInfo): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", firmwaresUrl, firmware);

	  console.log("StoreImpl/setFirmware/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getFirmwares();

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	// Get firmware for a project (verify/apply firmware to hotel rooms)
	async getProjectFirmware(hotelId: string): Promise<ProjectFirmwareResponse | null> {
	  const url = `/hotels.json?id=${encodeURIComponent(hotelId)}&command=firmware`;
	  const data = await apiOperation("GET", url, null);

	  console.log("StoreImpl/getProjectFirmware/hotelId=" + hotelId + "/data=", data);
	  if (!data.result) {
	    this.dispatchProjectFirmware(null);
	    return null;
	  }

	  // Dispatch to store
	  this.dispatchProjectFirmware(data.result);
	  return data.result;
	}

	dispatchProjectFirmware(firmware: ProjectFirmwareResponse | null) {
	  const payload = firmwaresSlice.actions.setProjectFirmware(firmware);
	  this.store.dispatch(payload);
	}

	clearProjectFirmware() {
	  const payload = firmwaresSlice.actions.clearProjectFirmware();
	  this.store.dispatch(payload);
	}

	async getTrackFormats() {
      const data = await apiOperation("GET", trackFormatsUrl, null);

      if (	!data ||
          	!data.result
         )
        return;

      this.dispatchTrackFormats(data.result);
    }

    dispatchTrackFormats(trackFormats: TrackFormatsHolder) {
      const payload =	trackFormatsSlice.actions.assign(trackFormats);

      this.store.dispatch(payload);
	}

	async setTrackFormats(trackFormats: TrackFormatsHolder): Promise<ErrorMessage | null> {
	  const data = await apiOperation("POST", trackFormatsUrl, trackFormats);

	  console.log("StoreImpl/setTrackFormats/data=", data);
	  if (!data.result) {
	    return null;
	  }

	  const mess = data.result;

	  if (	mess &&
  			mess.errors &&
  			mess.errors.length &&
  			mess.errors[0].code == 0
  		 )
	    this.getTrackFormats();

	  return	mess &&
	  			mess.errors &&
	  			mess.errors.length
	  			?	mess
      			:	null;
	}

	async getChannelsByTrackFormat(key: string): Promise<ChannelInfo[] | null> {
	  const url = `${trackFormatsUrl}?key=${encodeURIComponent(key)}`;
	  const data = await apiOperation("GET", url, null);
	  console.log("StoreImpl/getChannelsByTrackFormat/key=" + key + "/data=", data);
	  if (!data.result) {
	    return null;
	  }
	  return Array.isArray(data.result) ? data.result : [];
	}

	async getProjectsByTrackFormat(key: string): Promise<HotelInfo[] | null> {
	  if (!key) {
	    // An empty project_key returns the full catalog server-side, not 404,
	    // so never send one. Treat it as an empty result locally.
	    this.dispatchChannelListProjects([]);
	    return [];
	  }

	  const url = `${trackFormatsUrl}?project_key=${encodeURIComponent(key)}`;
	  try {
	    const response = await fetch(url, { credentials: 'include' });
	    console.log("StoreImpl/getProjectsByTrackFormat/key=" + key + "/status=" + response.status);

	    if (response.status == 404) {
	      // No visible project uses this key -> empty state, not an error.
	      this.dispatchChannelListProjects([]);
	      return [];
	    }

	    if (response.status == 401)
	      this.dispatchStatus(0);

	    if (!response.ok)
	      return null;

	    const res = await response.json();
	    const projects = (res && res.projects) || [];
	    this.dispatchChannelListProjects(projects);
	    return projects;
	  } catch(e) {
	    console.error("StoreImpl/getProjectsByTrackFormat/key=" + key + "/failed:", e);
	    return null;
	  }
	}

	async getAudit(params?: any) {
		let url = auditUrl;
		if (params) {
			const queryParams = new URLSearchParams();
			if (params.start_time) queryParams.append('start_time', params.start_time);
			if (params.end_time) queryParams.append('end_time', params.end_time);
			if (params.user) queryParams.append('user', params.user);
			if (params.type) queryParams.append('type', params.type);
			if (params.operation) queryParams.append('operation', params.operation);
			if (params.result) queryParams.append('result', params.result);
			if (params.id) queryParams.append('id', params.id);
			if (params.secondary_id) queryParams.append('secondary_id', params.secondary_id);
			if (params.search) queryParams.append('search', params.search);
			if (params.limit) queryParams.append('limit', params.limit.toString());
			const queryString = queryParams.toString();
			if (queryString) {
				url = `${auditUrl}?${queryString}`;
			}
		}
		const data = await apiOperation("GET", url, null)
		if (!data ||
			!data.result
		)
			return;
		this.dispatchAudit(data.result);
	}
	dispatchAudit(audit: AuditHolder) {
		const payload = auditSlice.actions.setAuditData(audit);
		this.store.dispatch(payload);
	}

	// ===== WebSocket Methods =====

	private ws: WebSocketService | null = null;
	private _refetchTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
	private _activeSubscriptions: SubscriptionTarget[] = [];
	private _currentRoomNo: string | null = null;
	private _currentChannelId: string | null = null;

	setCurrentRoom(roomNo: string | null): void {
	  this._currentRoomNo = roomNo;
	}

	setCurrentChannel(channelId: string | null): void {
	  this._currentChannelId = channelId;
	}

	connectWebSocket(): void {
	  if (this.ws) return;

	  this.ws = new WebSocketService({
	    onMessage: (msg) => this.handleWsMessage(msg),
	    onStatusChange: (status) => this.handleWsStatusChange(status),
	  });
	  this.ws.connect();
	}

	disconnectWebSocket(): void {
	  if (this.ws) {
	    this.ws.disconnect();
	    this.ws = null;
	  }
	  this._refetchTimers.forEach((timer) => clearTimeout(timer));
	  this._refetchTimers.clear();
	  this.clearSubscribeStagger();
	  this.clearSpeedTestTimeout();
	  this._activeSubscriptions = [];
	  this.store.dispatch(websocketSlice.actions.resetState());
	}

	private handleWsStatusChange(status: WebSocketConnectionStatus): void {
	  console.log('WS STATUS:', status);
	  this.store.dispatch(websocketSlice.actions.setStatus(status));

	  if (status === 'connected') {
	    this.resubscribeAll();
	  }

	  if (status === 'reconnecting') {
	    // A command still waiting for its ack will never get one on the new
	    // socket. One already acked keeps its timeout: the result is stored
	    // on the room whether or not this socket survives.
	    const speedTest = this.store.getState().websocket.speedTest;
	    if (speedTest.status === 'pending') {
	      this.store.dispatch(websocketSlice.actions.setSpeedTest({
	        ...speedTest,
	        status:		'error',
	        errorCode:	SPEED_TEST_NOT_CONNECTED,
	      }));
	    }

	    const state = this.store.getState();
	    if (state.logins.status === 0) {
	      console.log('WS: logged out, stopping reconnection');
	      this.disconnectWebSocket();
	    }
	  }
	}

	private handleWsMessage(msg: WSServerMessage): void {
	  console.log('WS MSG:', msg.type, msg);
	  switch (msg.type) {
	    case 'subscribed': {
	      const target = msg.target as SubscriptionTarget;
	      if (target && target.type) {
	        this.store.dispatch(websocketSlice.actions.addSubscription(target));
	      }
	      break;
	    }
	    case 'unsubscribed': {
	      const target = msg.target as SubscriptionTarget | undefined;
	      if (target && target.type) {
	        this.store.dispatch(websocketSlice.actions.removeSubscription(target));
	      } else {
	        this.store.dispatch(websocketSlice.actions.clearSubscriptions());
	      }
	      break;
	    }
	    case 'error': {
	      // §4.1 — the frame now also carries error:{code,message}. `target` is
	      // still the legacy human-readable mirror, so it stays the fallback for
	      // an older middleware that sends no `error` member.
	      const code = msg.error ? msg.error.code : '';
	      const errorMsg =	msg.error && msg.error.message
	      					?	msg.error.message
	      					:	(msg.target as string);

	      this.store.dispatch(websocketSlice.actions.setLastError(errorMsg));

	      // An error frame does not say which command it answers. While a
	      // speed test waits for its ack, every non-quiet error is its answer
	      // (subscribe refusals are all quiet codes). Shown on the room, not
	      // toasted.
	      const speedTest = this.store.getState().websocket.speedTest;
	      if (speedTest.status === 'pending' && !isQuietWsError(code)) {
	        this.store.dispatch(websocketSlice.actions.setSpeedTest({
	          ...speedTest,
	          status:		'error',
	          errorCode:	code || undefined,
	          error:		errorMsg,
	        }));
	        break;
	      }

	      // A refused subscribe is an EXPECTED answer: every subscribe is
	      // checked, the default is to refuse, and the connection stays open
	      // with nothing subscribed. Log it and move on — toasting it would
	      // greet an MW light user with an error on every page.
	      if (isQuietWsError(code)) {
	        console.log('WS SUBSCRIBE REFUSED (quiet): code=' + code + ' target=', msg.target);
	        break;
	      }

	      this.dispatchError(errorMsg);
	      break;
	    }
	    case 'session_expiring': {
	      // Backend warns us 5 min before the 25-min idle eviction. Seed
	      // a countdown so the SessionExpiringModal can prompt the user.
	      const target = msg.target as { seconds_until_logout?: number } | undefined;
	      const seconds = target && typeof target.seconds_until_logout === 'number'
	        ? target.seconds_until_logout
	        : 300;
	      this.store.dispatch(websocketSlice.actions.setSessionExpiring({
	        secondsUntilLogout: seconds,
	        receivedAt: Date.now(),
	      }));
	      break;
	    }
	    case 'speed_test': {
	      // The ack only means "queued for the room's TV sockets"; the result
	      // comes later through a room_info event, or not at all.
	      const speedTest = this.store.getState().websocket.speedTest;
	      if (msg.target === 'ok' && speedTest.status === 'pending') {
	        this.store.dispatch(websocketSlice.actions.setSpeedTest({
	          status:		'ack',
	          hotelId:		speedTest.hotelId,
	          roomNo:		speedTest.roomNo,
	          timestamp:	msg.timestamp ?? Math.floor(Date.now() / 1000),
	        }));
	        this.armSpeedTestTimeout();
	      }
	      break;
	    }
	    case ROOM_INFO_TYPE: {
	      this.handleRoomInfoNotification(msg);
	      break;
	    }
	    default: {
	      if ((CHANGE_NOTIFICATION_TYPES as readonly string[]).includes(msg.type)) {
	        this.handleChangeNotification(msg);
	      } else {
	        console.warn('WS UNHANDLED type:', msg.type, msg);
	      }
	      break;
	    }
	  }
	}

	private handleChangeNotification(msg: WSServerMessage): void {
	  console.log('WS CHANGE NOTIFICATION:', msg.type, 'target:', msg.target, 'user:', msg.user);

	  const currentUser = this.store.getState().logins?.login?.username;
	  if (msg.user && msg.user === currentUser) {
	    console.log('WS SKIP: notification from self (' + currentUser + ')');
	    return;
	  }

	  const target = msg.target as ChangeEventTarget;
	  const isHotelScoped = (HOTEL_SCOPED_TYPES as readonly string[]).includes(msg.type);

	  // For room notifications, only show if user is viewing that specific room
	  if (msg.type === 'room' && target?.secondary_id && this._currentRoomNo) {
	    if (target.secondary_id !== this._currentRoomNo) {
	      console.log('WS SKIP: room notification for room ' + target.secondary_id + ', currently viewing room ' + this._currentRoomNo);
	      return;
	    }
	  }

	  // For channel notifications, only show if user is viewing that specific channel
	  if (msg.type === 'channel' && target?.id && this._currentChannelId) {
	    if (target.id !== this._currentChannelId) {
	      console.log('WS SKIP: channel notification for channel ' + target.id + ', currently viewing channel ' + this._currentChannelId);
	      return;
	    }
	  }

	  const notification: PendingNotification = {
	    type: msg.type,
	    targetId: target?.id ?? '',
	    secondaryId: target?.secondary_id,
	    user: msg.user ?? 'unknown',
	    timestamp: msg.timestamp ?? Date.now() / 1000,
	    category: isHotelScoped ? 'project' : 'global',
	  };

	  this.store.dispatch(websocketSlice.actions.addPendingChange(notification));
	}

	private handleRoomInfoNotification(msg: WSServerMessage): void {
	  const target = msg.target as RoomInfoEventTarget;
	  const hotelId = target?.id ?? '';
	  const roomNo = target?.secondary_id ?? '';
	  if (!hotelId) return;

	  const currentUser = this.store.getState().logins?.login?.username;
	  if (msg.user && currentUser && msg.user === currentUser) {
	    console.log('WS room_info SKIP: notification from self (' + currentUser + ')');
	    return;
	  }

	  const key = 'room_info:' + hotelId + ':' + roomNo;
	  const existing = this._refetchTimers.get(key);
	  if (existing) clearTimeout(existing);
	  this._refetchTimers.set(key, setTimeout(() => {
	    this._refetchTimers.delete(key);
	    this.silentlyRefreshRoomInfo(hotelId, roomNo);
	  }, 300));
	}

	async silentlyRefreshRoomInfo(hotelId: string, roomNo: string): Promise<void> {
	  const speedTest = this.store.getState().websocket.speedTest;
	  const awaitsResult =	speedTest.status === 'ack' &&
	  						speedTest.hotelId === hotelId &&
	  						speedTest.roomNo === roomNo;

	  const active = this.store.getState().projects.active;
	  if ((!active || active.id !== hotelId) && !awaitsResult) return;

	  if (!roomNo) {
	    this.getRooms(hotelId);
	    return;
	  }

	  const room = await this.readRoomInfo(hotelId, roomNo);
	  if (room && awaitsResult)
	    this.checkSpeedTestResult(hotelId, roomNo, room);
	}

	// GET /rooms.json?id=&room_no= — the room carries its info fields (the
	// speed_test_* among them) under info.fields. Patches them into the open
	// project, and hands the room back.
	private async readRoomInfo(hotelId: string, roomNo: string): Promise<HotelRoom | null> {
	  const url = `${roomsUrl}?id=${encodeURIComponent(hotelId)}&room_no=${encodeURIComponent(roomNo)}`;
	  const data = await apiOperation("GET", url, null);
	  if (!data || !data.result) return null;

	  const room = data.result as HotelRoom;
	  this.store.dispatch(projectsSlice.actions.patchRoomInfo({
	    hotelId,
	    roomNo,
	    room,
	  }));
	  return room;
	}

	private _speedTestTimer: ReturnType<typeof setTimeout> | null = null;

	private clearSpeedTestTimeout(): void {
	  if (this._speedTestTimer) {
	    clearTimeout(this._speedTestTimer);
	    this._speedTestTimer = null;
	  }
	}

	// The server sets no timeout and the room_info event can be dropped by
	// the 1 s throttle: after a while read the room once more ourselves.
	private armSpeedTestTimeout(): void {
	  this.clearSpeedTestTimeout();
	  this._speedTestTimer = setTimeout(async () => {
	    this._speedTestTimer = null;
	    const st = this.store.getState().websocket.speedTest;
	    if (st.status !== 'ack' || !st.hotelId || !st.roomNo) return;

	    const room = await this.readRoomInfo(st.hotelId, st.roomNo);
	    if (room && this.checkSpeedTestResult(st.hotelId, st.roomNo, room)) return;

	    const now = this.store.getState().websocket.speedTest;
	    if (now.status !== 'ack' || now.hotelId !== st.hotelId || now.roomNo !== st.roomNo) return;
	    this.store.dispatch(websocketSlice.actions.setSpeedTest({
	      ...now,
	      status:		'error',
	      errorCode:	SPEED_TEST_NO_RESULT,
	    }));
	  }, SPEED_TEST_TIMEOUT_MS);
	}

	private checkSpeedTestResult(hotelId: string, roomNo: string, room: HotelRoom): boolean {
	  const st = this.store.getState().websocket.speedTest;
	  if (st.status !== 'ack' || st.hotelId !== hotelId || st.roomNo !== roomNo) return false;

	  const result = readSpeedTestResult(extractInfoFields(room));
	  if (!isNewSpeedTestResult(result, st.timestamp)) return false;

	  this.clearSpeedTestTimeout();
	  this.store.dispatch(websocketSlice.actions.setSpeedTest({ ...st, status: 'done' }));
	  return true;
	}

	acceptRefresh(): void {
	  const state = this.store.getState();
	  const pending = state.websocket.pendingChanges;
	  console.log('WS acceptRefresh: pending=', pending);

	  const actions = new Set<string>();

	  for (const n of pending) {
	    switch (n.type) {
	      case 'project':
	      case 'project_settings':
	      case 'room':
	        actions.add('project:' + n.targetId);
	        break;
	      case 'channel': {
	        // n.targetId is the channel name/epg_id, NOT the hotel_id
	        // Use secondaryId (hotel_id from notification) or the active subscription scope
	        const channelHotelId = n.secondaryId ??
	          this._activeSubscriptions.find(s => s.type === 'channel')?.scope_id;
	        if (channelHotelId) {
	          actions.add('channels:' + channelHotelId);
	        }
	        actions.add('channels:global');
	        break;
	      }
	      case 'local_channel_list':
	        actions.add('channelLists:' + n.targetId);
	        break;
	      case 'global_channel_list':
	        actions.add('channelLists:global');
	        break;
	      case 'language':
	        actions.add('languages');
	        break;
	      case 'tag':
	        actions.add('tags');
	        break;
	      case 'app':
	        actions.add('apps');
	        break;
	      case 'firmware':
	        actions.add('firmwares');
	        break;
	      case 'user':
	        actions.add('logins');
	        break;
	      case 'track_format':
	        actions.add('trackFormats');
	        break;
	      case 'project_group':
	        actions.add('projectGroups');
	        break;
	    }
	  }

	  console.log('WS acceptRefresh: actions=', [...actions]);

	  for (const action of actions) {
	    if (action.startsWith('project:')) {
	      const hotelId = action.split(':')[1];
	      if (hotelId) {
	        this.getProjects(hotelId);
	        this.getProjects(); // also refresh global project list
	      }
	    } else if (action.startsWith('channels:')) {
	      const hotelId = action.split(':')[1];
	      const login = this.store.getState().logins.login;
	      const canViewGlobalChannels = login && (login.role === 'admin' || login.privileges?.includes('ViewChannels') || login.privileges?.includes('ChangeChannels'));
	      if (hotelId === 'global') {
	        if (canViewGlobalChannels) this.getChannels();
	      } else {
	        this.getChannels(hotelId);
	      }
	    } else if (action.startsWith('channelLists:')) {
	      const hotelId = action.split(':')[1];
	      if (hotelId === 'global') {
	        this.getChannelLists();
	      } else {
	        this.getChannelLists(hotelId);
	      }
	    } else if (action === 'languages') {
	      this.getLanguages();
	    } else if (action === 'tags') {
	      this.getTags();
	    } else if (action === 'apps') {
	      const login = this.store.getState().logins.login;
	      if (login && (login.role === 'admin' || login.privileges?.includes('ViewApps') || login.privileges?.includes('ChangeApps') || login.privileges?.includes('SeeAllProjects')))
	        this.getApps();
	    } else if (action === 'firmwares') {
	      this.getFirmwares();
	    } else if (action === 'logins') {
	      this.getLogins();
	    } else if (action === 'trackFormats') {
	      this.getTrackFormats();
	    } else if (action === 'projectGroups') {
	      this.refreshProjectGroups();
	      this.getProjects();
	    }
	  }

	  this.store.dispatch(websocketSlice.actions.clearPendingChanges());
	}

	dismissRefresh(): void {
	  this.store.dispatch(websocketSlice.actions.clearPendingChanges());
	}

	subscribeTo(targets: SubscriptionTarget[]): void {
	  if (this._activeSubscriptions.length > 0 && this.ws && this.ws.isConnected()) {
	    this.ws.unsubscribe();
	  }

	  this._activeSubscriptions = targets;
	  this.store.dispatch(websocketSlice.actions.clearSubscriptions());
	  this.store.dispatch(websocketSlice.actions.clearPendingChanges());

	  console.log('WS subscribeTo:', JSON.stringify(targets));
	  this.sendSubscribes(targets);
	}

	unsubscribeAll(): void {
	  this.clearSubscribeStagger();

	  if (this.ws && this.ws.isConnected()) {
	    this.ws.unsubscribe();
	  }
	  this._activeSubscriptions = [];
	  this.store.dispatch(websocketSlice.actions.clearSubscriptions());
	  this.store.dispatch(websocketSlice.actions.clearPendingChanges());
	}

	private resubscribeAll(): void {
	  console.log('WS RESUBSCRIBE:', JSON.stringify(this._activeSubscriptions));
	  this.store.dispatch(websocketSlice.actions.clearSubscriptions());
	  this.sendSubscribes(this._activeSubscriptions);
	}

	private _subscribeTimers: ReturnType<typeof setTimeout>[] = [];

	private clearSubscribeStagger(): void {
	  for (const timer of this._subscribeTimers)
	    clearTimeout(timer);

	  this._subscribeTimers = [];
	}

	// §4.3 — the server allows 20 subscribe frames per second per connection and
	// closes the socket (1008) on the 21st. No page asks for that many today,
	// but the cap is per CONNECTION, so a reconnect that replays every scope
	// plus a page change landing in the same second could reach it. Frames go
	// out in bursts well under the limit, with a gap between them.
	private sendSubscribes(targets: SubscriptionTarget[]): void {
	  this.clearSubscribeStagger();

	  if (!this.ws || !this.ws.isConnected()) return;

	  const send = (batch: SubscriptionTarget[]) => {
	    if (!this.ws || !this.ws.isConnected()) return;
	    for (const target of batch) {
	      this.ws.subscribe(target);
	    }
	  };

	  if (targets.length <= WS_SUBSCRIBE_BURST) {
	    send(targets);
	    return;
	  }

	  for (let i = 0; i < targets.length; i += WS_SUBSCRIBE_BURST) {
	    const batch = targets.slice(i, i + WS_SUBSCRIBE_BURST);
	    const wait = (i / WS_SUBSCRIBE_BURST) * WS_SUBSCRIBE_BURST_GAP_MS;

	    if (wait === 0) {
	      send(batch);
	      continue;
	    }

	    this._subscribeTimers.push(setTimeout(() => send(batch), wait));
	  }
	}

	// POST /skyway_refresh.json — queues an out-of-band reload of the device
	// inventory cache. Never blocks; the snapshot describes the cache BEFORE
	// the reload lands. The caller branches on `status` for 403/503.
	async skywayRefresh(): Promise<SkywayRefreshResult> {
	  const data = await apiOperation("POST", skywayRefreshUrl, null);

	  if (!data.result) {
	    return {
	      ok:		false,
	      status:	data.response ? data.response.status : 0,
	    };
	  }

	  const res = data.result;

	  return {
	    ok:	true,
	    snapshot: {
	      queued:		!!res.queued,
	      total:		typeof res.total === "number" ? res.total : 0,
	      last_update:	res.last_update || "",
	      source:		res.source || "empty",
	      error:		res.error || undefined,
	    },
	  };
	}

	async skywayCommand(hotelId: string, roomNo: string, action: "reboot" | "standby"): Promise<boolean> {
	  const url = `/skyway.json?${action}=1`;
	  const data = await apiOperation("POST", url, { hotel_id: hotelId, room_no: roomNo });

	  console.log("StoreImpl/skywayCommand/action=" + action + "/data=", data);
	  if (!data.result || data.result.status !== "ok") {
	    return false;
	  }

	  this.dispatchSuccess(`Device ${action} command sent`);
	  return true;
	}

	// WS /ws/message {type:"speed_test", target:{id, room_no}} — both must be
	// non-empty strings. The answer is the ack or an error frame (see the
	// 'speed_test' and 'error' cases of handleWsMessage).
	startSpeedTest(hotelId: string, roomNo: string): void {
	  if (!hotelId || !roomNo) return;

	  this.clearSpeedTestTimeout();

	  if (!this.ws || !this.ws.isConnected()) {
	    this.store.dispatch(websocketSlice.actions.setSpeedTest({
	      status:		'error',
	      hotelId,
	      roomNo,
	      errorCode:	SPEED_TEST_NOT_CONNECTED,
	    }));
	    return;
	  }

	  this.store.dispatch(websocketSlice.actions.setSpeedTest({
	    status: 'pending',
	    hotelId,
	    roomNo,
	  }));

	  this.ws.send({
	    type: 'speed_test',
	    target: { id: String(hotelId), room_no: String(roomNo) },
	  });
	}

	/*async performCommand(command: string): Promise<CommandInfo | null> {
	  const cmd: CommandInfo = {
        command:				command,
      };
      
      try {
        const data = await apiOperation("POST", "/command.json", cmd);
	    console.log("StoreImpl/performCommand/POST/data=", data);
	    
	    return data;
	  } catch(e) {
	    console.error("StoreImpl/performCommand/POST/Got error:", e);
	  }
	  
	  return null;
	}*/
	
}

export const store = new StoreImpl();

export type RootState = ReturnType<typeof store.store.getState>;

export type AppDispatch = typeof store.store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

